// Adapted from OpenCharts (MIT, see LICENSE-OpenCharts), ported to the
// lightweight-charts v5 primitive API.
import type {
  IChartApi, IPrimitivePaneRenderer, IPrimitivePaneView, ISeriesPrimitive, ISeriesPrimitiveAxisView,
  SeriesAttachedParameter, Time,
} from "lightweight-charts";
import { readableInk } from "./geometry";
import { renderEntry } from "./renderers";
import { type AnySeries, makeResolveCtx, resolveEntry } from "./resolve";
import type { Drawing, DrawInfo, DrawTheme, EntryState, ResolvedEntry } from "./types";

type Target = Parameters<IPrimitivePaneRenderer["draw"]>[0];

/** A drawing's price on the price scale, in its colour. */
class PriceAxisLabel implements ISeriesPrimitiveAxisView {
  private y: number | null = null;

  constructor(
    private readonly source: DrawingsPrimitive,
    private readonly price: number,
    private readonly color: string,
  ) {}

  update(): void {
    this.y = this.source.seriesOrNull()?.priceToCoordinate(this.price) ?? null;
  }

  coordinate(): number {
    return this.y ?? -1;
  }

  visible(): boolean {
    return this.y !== null;
  }

  tickVisible(): boolean {
    return true;
  }

  text(): string {
    return this.source.seriesOrNull()?.priceFormatter().format(this.price) ?? "";
  }

  textColor(): string {
    return readableInk(this.color);
  }

  backColor(): string {
    return this.color;
  }
}

/**
 * One series primitive that paints every drawing (plus the one being placed)
 * in a single pass, above the candles and EMAs. Hover / selection state is
 * pushed in by DrawingManager.
 */
export class DrawingsPrimitive implements ISeriesPrimitive<Time> {
  private chart: IChartApi | null = null;
  private series: AnySeries | null = null;
  private requestUpdate: (() => void) | null = null;
  private drawings: Drawing[] = [];
  private preview: Drawing | null = null;
  private selectedIds = new Set<string>();
  private hoveredId: string | null = null;
  private intervalSec = 3600;
  private riskUsd = 0;
  private rightLane = 0;
  private entries: ResolvedEntry[] = [];
  private axisViews: PriceAxisLabel[] = [];
  private readonly views: readonly IPrimitivePaneView[];

  constructor(private theme: DrawTheme) {
    const renderer: IPrimitivePaneRenderer = {
      draw: (target: Target) => {
        const info = this.drawInfo();
        target.useBitmapCoordinateSpace(scope => {
          for (const e of this.entries) renderEntry(scope, e, info);
        });
      },
    };
    this.views = [{ zOrder: () => "top", renderer: () => renderer }];
  }

  attached({ chart, series, requestUpdate }: SeriesAttachedParameter<Time>) {
    this.chart = chart as IChartApi;
    this.series = series;
    this.requestUpdate = requestUpdate;
  }

  detached() {
    this.chart = null;
    this.series = null;
    this.requestUpdate = null;
  }

  seriesOrNull(): AnySeries | null {
    return this.series;
  }

  updateAllViews(): void {
    this.entries = this.resolveEntries();
    for (const v of this.axisViews) v.update();
  }

  paneViews() {
    return this.views;
  }

  priceAxisViews() {
    return this.axisViews;
  }

  setDrawings(drawings: Drawing[]): void {
    this.drawings = drawings;
    this.rebuildAxisViews();
    this.requestUpdate?.();
  }

  setPreview(d: Drawing | null): void {
    this.preview = d;
    this.requestUpdate?.();
  }

  setSelected(ids: readonly string[]): void {
    this.selectedIds = new Set(ids);
    this.rebuildAxisViews();
    this.requestUpdate?.();
  }

  setHovered(id: string | null): void {
    if (this.hoveredId === id) return;
    this.hoveredId = id;
    this.requestUpdate?.();
  }

  setIntervalSec(intervalSec: number): void {
    if (this.intervalSec === intervalSec) return;
    this.intervalSec = intervalSec;
    this.requestUpdate?.();
  }

  setRiskUsd(riskUsd: number): void {
    if (this.riskUsd === riskUsd) return;
    this.riskUsd = riskUsd;
    this.requestUpdate?.();
  }

  setRightLane(px: number): void {
    if (this.rightLane === px) return;
    this.rightLane = px;
    this.requestUpdate?.();
  }

  private drawInfo(): DrawInfo {
    const series = this.series;
    return {
      theme: this.theme,
      riskUsd: this.riskUsd,
      rightLane: this.rightLane,
      priceFormat: p => series?.priceFormatter().format(p) ?? String(p),
    };
  }

  private resolveEntries(): ResolvedEntry[] {
    if (!this.chart || !this.series) return [];
    const ctx = makeResolveCtx(this.chart, this.series, this.intervalSec);
    const entries = this.drawings.map(d => resolveEntry(d, ctx, this.stateFor(d.id)));
    if (this.preview) entries.push(resolveEntry(this.preview, ctx, "preview"));
    return entries;
  }

  private stateFor(id: string): EntryState {
    if (this.selectedIds.has(id)) return "selected";
    if (id === this.hoveredId) return "hovered";
    return "normal";
  }

  // Horizontal lines always show their price on the axis; other drawings show
  // their anchor prices while selected (TradingView behaviour).
  private rebuildAxisViews(): void {
    const views: PriceAxisLabel[] = [];
    for (const d of this.drawings) {
      if (d.type === "horizontal") {
        views.push(new PriceAxisLabel(this, d.price, d.color));
        continue;
      }
      if (!this.selectedIds.has(d.id)) continue;
      if (d.type === "position") {
        views.push(new PriceAxisLabel(this, d.price, this.theme.text));
        if (d.targetPrice != null) views.push(new PriceAxisLabel(this, d.targetPrice, this.theme.profit));
        if (d.stopPrice != null) views.push(new PriceAxisLabel(this, d.stopPrice, this.theme.loss));
        continue;
      }
      if (Number.isFinite(d.price)) views.push(new PriceAxisLabel(this, d.price, d.color));
      if (d.price2 != null) views.push(new PriceAxisLabel(this, d.price2, d.color));
    }
    this.axisViews = views;
  }
}
